<template>
    <v-app-bar
        :id="$vuetify.breakpoint.mdAndUp ? 'app-toolbar' : 'app-toolbar-mobile'"
        :class="isMiniUku ? 'mx-3 mt-4 hideOnMiniUkuClose' : ''"
        class="global-elevation"
        extension-height="500px"
        height="56px"
        clipped-left
        color='white'
        app
    >
        <v-row
            no-gutters
            align="center"
            class="h-100 flex-nowrap"
        >
            <v-col class="shrink pr-3">
                <icon-button
                    v-if="$route.meta.parent_path && $vuetify.breakpoint.smAndDown"
                    icon="ChevronLeft"
                    @click="routerPush($route.meta.parent_path)"
                />
                <router-link
                    v-else
                    :to="{ name: 'dashboard' }"
                    @click.native="navigateDashboard"
                >
                    <v-img
                        max-height="32px"
                        max-width="25px"
                        contain
                        :src="require('../../assets/uku-logo-colored.svg')"
                    />
                </router-link>
            </v-col>
            <v-col
                v-if="shouldShowTitle($route)"
                class="shrink"
                :class="$vuetify.breakpoint.smAndDown ? 'toolbar-title-mobile' : ''"
            >
                <v-toolbar-title
                    id="app-bar-title"
                    class="main--text title"
                    :class="$vuetify.breakpoint.mdAndUp ? 'mr-12' : ''"
                >
                    <template
                        v-if="breadcrumbsViewName === $route.name"
                    >
                        <span
                            v-for="(breadcrumb, index) in breadcrumbs"
                            :key="index"
                        >
                            <router-link
                                v-if="breadcrumb.to"
                                :to="breadcrumb.to"
                                class="main--text"
                                style="text-decoration: none"
                            >
                                {{ breadcrumb.name }}
                            </router-link>
                            <span
                                v-else
                                class="main--text"
                            >
                                {{ breadcrumb.name }}
                            </span>
                            <span
                                v-if="index < breadcrumbs.length - 1"
                                class="mx-2"
                            >›</span>
                        </span>
                    </template>
                    <template
                        v-else-if="($route.meta.parent_path && $vuetify.breakpoint.smAndDown) || $route.meta.dynamicTitle"
                    >
                        <span>{{ page_title ? page_title : "" }}</span>
                    </template>
                    <template v-else>
                        <span>{{ $route.meta.title ? $t($route.meta.title) : "" }}</span>
                    </template>
                </v-toolbar-title>
            </v-col>
            <v-col
                v-if="showCalendar()"
                class="pa-0 mr-2 shrink"
                style="flex-basis: content"
            >
                <AppToolbarCalendar
                    :is-mobile="$vuetify.breakpoint.smAndDown"
                    :calendar="calendar"
                    :show-today-button="calendar || $route.name === 'calendar'"
                    :inverted="calendar || $route.name === 'calendar'"
                    :unit="$route.name === 'dashboard' || $route.name === 'todo' ? '' : 'week'"
                    :range="$route.name === 'dashboard' || $route.name === 'todo' ? 1 : 2"
                    @update:calendar="$emit('update:calendar', $event)"
                />
            </v-col>
            <v-expand-x-transition>
                <div
                    v-show="$vuetify.breakpoint.mdAndUp && isDashboard()"
                    class="mr-2 overflow-hidden"
                    style="max-width: 225px; width: 100%"
                >
                    <AppToolbarSearch />
                </div>
            </v-expand-x-transition>
            <template v-if="shouldShowNavigation() && $vuetify.breakpoint.mdAndUp">
                <v-spacer></v-spacer>
                <v-col
                    class="pa-0 mr-2 shrink"
                >
                    <AppToolbarNavigation />
                </v-col>
            </template>

            <v-spacer></v-spacer>
            <v-col
                v-if="$vuetify.breakpoint.mdAndUp"
                class="pa-0 shrink mr-2"
            >
                <AppToolbarTracking />
            </v-col>
            <v-col
                v-if="$vuetify.breakpoint.mdAndUp"
                class="pa-0 shrink mr-2"
            >
                <AppToolbarNewTask v-slot="{ on }">
                    <template>
                        <v-btn
                            rounded
                            color="new-task"
                            class="round-btn"
                            test-id="new-task-button"
                            v-on="on"
                            @click="newTask"
                        >
                            <icon icon="Plus" />
                        </v-btn>
                    </template>
                </AppToolbarNewTask>
            </v-col>
            <v-col class="pa-0 shrink mr-2">
                <AppToolbarNotifications :is-mobile="$vuetify.breakpoint.smAndDown" />
            </v-col>
            <v-col class="pa-0 shrink mr-4">
                <AppToolbarAccountMenu :is-mobile="$vuetify.breakpoint.smAndDown" />
            </v-col>
            <AppToolbarDateTime v-if="$vuetify.breakpoint.mdAndUp" />
        </v-row>
    </v-app-bar>
</template>

<script>
import AppToolbarDateTime from "./AppToolbarDateTime";
import AppToolbarNewTask from "./AppToolbarNewTask";
import AppToolbarCalendar from "./AppToolbarCalendar";
import AppToolbarSearch from "./AppToolbarSearch";
import AppToolbarTracking from "./AppToolbarTracking";
import AppToolbarNotifications from "./AppToolbarNotifications";
import AppToolbarAccountMenu from "./AppToolbarAccountMenu";
import AppToolbarNavigation from "./AppToolbarNavigation";
import router from "../../router";
import AppModule from "../../store/modules/app";
import ProjectModule from "@/store/modules/project";
import TaskModule from "@/store/modules/task";
import { convertQueryStringToParamsObject, getFullQuery } from "@/components/NewFilterBar/FilterBar";
import { getters as bookmarkGetters } from "@/store/modules/bookmark";
import BreadcrumbsModule from "@/store/modules/breadcrumbs";

export default {
    name: "AppToolbar",
    components: {
        AppToolbarDateTime,
        AppToolbarNotifications,
        AppToolbarNewTask,
        AppToolbarCalendar,
        AppToolbarSearch,
        AppToolbarTracking,
        AppToolbarAccountMenu,
        AppToolbarNavigation
    },
    props: {
        drawer: {
            type: Boolean,
            default: true
        },
        calendar: {
            type: Boolean,
            default: false
        },
        isMiniUku: {
            type: Boolean,
            default: false
        },
        isMobile: {
            type: Boolean,
            default: false
        }
    },
    setup(props) {
        const { openTask } = new TaskModule();
        const { breadcrumbs, breadcrumbsViewName } = BreadcrumbsModule();
        const appModule = new AppModule()
        const projectModule = new ProjectModule()

        const isTest = process.env.VUE_APP_TEST === "true";
        const isBeta = process.env.VUE_APP_BETA === "true";

        const routerPush = function(name) {
            page_title.value = null;

            if (name === "/") {
                let my_route = bookmarkGetters.dashboard_bookmark_route.value;

                if (my_route) return appModule.navigateReload(my_route);
                return appModule.navigateReload(my_route)
            } else {
                // console.log("router.push( { name: name } )", name)
                return router.push({ name: name }).catch(() => {});
            }
        };

        const isDashboard = function(){
            let params = convertQueryStringToParamsObject(getFullQuery())
            return router.currentRoute.name === 'dashboard' && !params['searchMode']
        }

        const showCalendar = function(){
            return router.currentRoute.name === 'calendar'
                || isDashboard()
                || router.currentRoute.name === 'todo';
        }

        const { page_title } = AppModule();

        const shouldShowTitle = route => {
            if ((route.meta.parent_path && page_title) || route.meta.title || route.meta.dynamicTitle) {
                if (route.name === "dashboard" && props.isMobile) {
                    return false;
                } else {
                    return true;
                }
            }
        };

        const newTask = function() {
            let props = {};

            if (router.currentRoute.meta && router.currentRoute.meta.newTaskSetProjectId && projectModule.current_project.value) {
                props.project_id = projectModule.current_project.value.id;
                props.client_id = projectModule.current_project.value.client_id;
            } else if (router.currentRoute.name.startsWith("client_")) {
                props.client_id = parseInt(router.currentRoute.params.id);
            }

            openTask(0, props);
        };

        const shouldShowNavigation = () => {
            return router.currentRoute.meta.headerNavigation && localStorage.getItem('cached_list')
        }

        const navigateDashboard = (e) => {
            e.preventDefault();
            appModule.navigateReload({ name: 'dashboard' })

            return false;
        }

        return {
            routerPush,
            page_title,
            shouldShowTitle,
            isTest,
            isBeta,
            showCalendar,
            isDashboard,
            newTask,
            breadcrumbs,
            breadcrumbsViewName,
            shouldShowNavigation,
            appModule,
            navigateDashboard
        };
    }
};
</script>
